import React, { useEffect } from 'react';
import { observer } from 'mobx-react';

import {
  AccordionItem,
  Heading,
  Paragraph,
  Box,
  Button,
  SvgIcon,
} from 'components';
import { InfoIcon } from 'icons';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import {
  DfmFeatureIssues,
  DfmStatus,
} from 'libs/rendering/meshAnalysis/appManager';

import { useAppManager } from 'core/hooks';
import { RFQApp, JobSpecModel } from 'apps/rfq/app';
import { getDfmFeatureIcon } from 'apps/rfq/dfmConfig';
import { confToFields } from 'libs/forms';
import { BuildValueModel } from 'generated/models';
import { DFMInfo } from './DFMInfo';
import { ErrorItem } from './ErrorItem';
import { DfmIssuesStyled } from './dfmIssues.styled';
import { DFMIssuesAppManager } from './appManager';
import { DfmSummary } from './DfmSummary';

// [jobSpec] in [RFQApp] is not a "visible" property
interface RFQAppExtended extends RFQApp {
  jobSpec: JobSpecModel;
}

interface DFMIssuesProps {
  rfqApp: RFQAppExtended; // the RFQ (null if DFM tool, see below [toolUnits])
  body: any; // the analyzed Body (wasm object)
  toolUnits?: 'mm' | 'cm' | 'inch';
}

export const DFMIssues = observer(
  ({ rfqApp, body, toolUnits }: DFMIssuesProps) => {
    const viewerControl = useViewerControl();
    const { lastCutoffAnalysis, isWaiting, hasFinished, dfmError } =
      viewerControl.meshAnalysis;
    // DFMIssuesAppManager for querying the DB for available processes.
    // Stores the currently analyzed fabrication process.
    const dfmIssuesAppManager = useAppManager(
      DFMIssuesAppManager,
      null,
      { rfqApp },
      true,
      [rfqApp],
    );

    // get status from [lastCutoffAnalysis]
    const dfmStarted = isWaiting || hasFinished;
    const isAvailable = lastCutoffAnalysis !== null;
    const hasIssues = isAvailable && lastCutoffAnalysis.hasIssues;
    const hasProcess = isAvailable && !lastCutoffAnalysis.isEmpty;

    const isTool = Boolean(toolUnits);

    // get the RFQ process and units.
    const build = rfqApp?.jobSpec?.build;
    const rfqProcess = build?.process;
    const units = build?.values
      ? build?.values.find((obj: BuildValueModel) => {
          return obj.of.name === 'Unit';
        })?.valueString
      : toolUnits;

    /*
      effects
    */

    // unmount => clear cutoff analysis results,
    //  so mouse selection goes back to 'normal' state
    useEffect(() => {
      return () => {
        if (body?.isReadyForDfmCutoff) {
          // clear previous dfm selection
          viewerControl.viewer?.selectionManager?.ClearDfmSelection();
          // clear last cutoff analysis
          body.GetDfm()?.Clear();
        }
      };
    }, []);

    // new body => request new DFM/cutoff analysis
    // NOTE: disbaled by now for RFQs. During the testing period, to start an
    //       analysis the user needs to click the button [Run Analysis].
    useEffect(() => {
      if (body && !body.isReadyForDfmCutoff) {
        if (isTool) {
          // automatic DFM analysis only for standalone tool by now...
          viewerControl.RequestDfmAnalysis(body);
        }
      }
    }, [body]);

    // new DFM results / process change => request new cutoff analysis
    useEffect(() => {
      if (
        !dfmError &&
        body?.isReadyForDfmCutoff &&
        // needed when switching between RFQs of the same body
        (viewerControl.meshAnalysis.dfmStatus === DfmStatus.DONE ||
          viewerControl.meshAnalysis.dfmStatus === DfmStatus.CUTOFF)
      ) {
        viewerControl.RequestCutoffAnalysis(
          body,
          dfmIssuesAppManager.dfmProcess?.name,
          units,
        );
      }
    }, [
      viewerControl.meshAnalysis.dfmStatus,
      dfmError,
      dfmIssuesAppManager.dfmProcess,
      units,
    ]);

    useEffect(() => {
      if (!dfmIssuesAppManager.dfmProcess) dfmIssuesAppManager.SetProcess(null);
    }, [dfmIssuesAppManager.dfmProcess]);

    /*
      render
    */

    // <Button/> to highlight/select all issues
    // (for inner <AccordionItem/>)
    const getFeatureHighlightButton = (
      dfmFeatIssues: DfmFeatureIssues,
      hasInnerHighlights: boolean,
    ) => {
      return dfmFeatIssues ? (
        <Button
          icon='view'
          iconPosition='right'
          appearance='plain'
          margin='0 0.5rem 0 0'
          variant={hasInnerHighlights ? 'primary' : 'tertiary'}
          size='small'
          onClick={() => {
            viewerControl.SelectDfmWarnings(dfmFeatIssues.type);
          }}
          onMouseEnter={() => {
            viewerControl.HighlightDfmWarnings(dfmFeatIssues.type);
          }}
          onMouseLeave={viewerControl.UnHighlightEverything}
        />
      ) : null;
    };

    // "DFM Issues" message for RFQ form
    const msg = () => {
      if (!dfmStarted) {
        // no DFM analysis started yet...
        return 'Start an analysis to look for potential DFM Issues.';
      }

      if (dfmError) {
        // server error
        return 'Sorry, an error occurred during the DFM analysis for this RFQ.';
      }

      if (isWaiting) {
        // still running on the server...
        return 'A DFM analysis for this RFQ is currently running...';
      }

      if (!lastCutoffAnalysis) {
        // This means that there're no cutoffs for the current process,
        //  so we cannot proceed with the cutoff analysis.
        // There's a special message for this case.
        return null;
      }

      if (lastCutoffAnalysis.hasIssues) {
        // DFM Issues found
        return (
          'Please review the following potential DFM Issues before ' +
          'quoting and talk to customers if neccesary.'
        );
      }

      // no DFM Issues found
      return hasProcess
        ? 'No DFM Issues found for this fabrication process.'
        : 'Please, choose a fabrication process from the above dropdown.';
    };

    if (!body) return null;

    return (
      <DfmIssuesStyled isTool={isTool}>
        <Box>
          {/* TOOL-only: Manufacturing Features + Fabrication Process Selection */}
          {isTool && !dfmError && hasFinished && (
            <Box mt={2} mb={3}>
              <Box sx={{ display: 'flex' }}>
                <DfmSummary
                  key='dfm_report'
                  body={body}
                  processId={body.GetDfm()?.processId}
                  units={units}
                  title='Manufacturing Features'
                  defaultExpanded
                  bold
                />
              </Box>
              <Box sx={{ minWidth: '300px' }}>
                {confToFields({
                  // DB query to get manufacturing processes
                  formStore: dfmIssuesAppManager.dfmProcessFormStore,
                  refs: {},
                })}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex' }}>
            <Heading
              level={6}
              size={toolUnits ? 'default' : 'large'}
              weight='xbold'
            >
              DFM Issues
            </Heading>
          </Box>

          {/* DFM status message */}
          {/* NOTE: during testing phase, analysis starts by clicking below button,
                    not automatically in above (disabled) effect */}
          {(!isTool || !lastCutoffAnalysis?.hasIssues) && (
            <Box mt={2} mb={2} sx={{ display: 'flex' }}>
              <Paragraph>{msg()}</Paragraph>
              {!dfmStarted && (
                <div>
                  <Button
                    text='Run Analysis'
                    icon='play'
                    iconPosition='right'
                    variant='tertiary'
                    size='small'
                    outline
                    onClick={() => viewerControl.RequestDfmAnalysis(body)}
                  />
                </div>
              )}
            </Box>
          )}

          {/* DFM Issues list */}
          <Box>
            {/* for each DFM feature ("Wall Thickness", "Hole Radius") */}
            {lastCutoffAnalysis?.featureIssues.map(
              (dfmFeatIssues: DfmFeatureIssues, ix: number) => {
                if (dfmFeatIssues && dfmFeatIssues.warningCount > 0) {
                  const dfmEnum = viewerControl?.viewer?.module.DfmAnalysisType;
                  const featIcon = getDfmFeatureIcon(
                    dfmFeatIssues.type,
                    dfmEnum,
                  );
                  const { title, warningCount, wasmObj, warningList } =
                    dfmFeatIssues;
                  const hasInnerHighlights =
                    viewerControl.hasDfmHighlights(wasmObj) ||
                    viewerControl.hasDfmSelections(wasmObj);
                  return (
                    <AccordionItem
                      key={`dfmFeat_${ix}`}
                      icon={featIcon}
                      iconSize='xlarge'
                      divisor
                      title={title}
                      subTitle={`(${warningCount})`}
                      innerHighlight={hasInnerHighlights}
                      button={getFeatureHighlightButton(
                        dfmFeatIssues,
                        hasInnerHighlights,
                      )}
                    >
                      {/* for each DFM feature issue */}
                      <Box>
                        {warningList.map((issue, key) => (
                          <ErrorItem
                            key={`dfmFeat_${ix}_${key}`}
                            warning={issue}
                            icon={featIcon}
                          />
                        ))}
                      </Box>
                    </AccordionItem>
                  );
                }
                return null;
              },
            )}
          </Box>
        </Box>

        {/* RFQ-only: DFM Issues message */}
        {!isTool && !isWaiting && dfmStarted && (
          <DFMInfo
            dfmIssuesAppManager={dfmIssuesAppManager}
            hasIssues={hasIssues}
            rfqProcess={rfqProcess}
            isAvailable={isAvailable}
          />
        )}

        {/* TOOL-only: no available DFM analysis message */}
        {isTool && !isAvailable && (
          <Box pb={4} sx={{ display: 'flex' }}>
            <SvgIcon icon={<InfoIcon />} size='medium' />
            <Paragraph ml={2}>
              Sorry, there is no available DFM analysis for the selected
              process.
            </Paragraph>
          </Box>
        )}
      </DfmIssuesStyled>
    );
  },
);
